import { DIFFICULTY_CONFIG, type DifficultyConfig } from '../../config/difficulty.config';
import { LEVEL_CONFIG, type LevelConfig } from '../../config/level.config';
import { PLAYER_CONFIG, type PlayerConfig } from '../../config/player.config';
import { RENDERING_CONFIG } from '../../config/rendering.config';
import { WORLD_CONFIG, type WorldConfig } from '../../config/world.config';
import { deriveJumpPhysics, type JumpPhysics } from '../../physics/JumpPhysics';

/**
 * Everything a segment layout must respect, derived from the player physics and the
 * difficulty ramp: how high the runner steps, jumps and slides, how far a jump carries
 * at start speed, how fast lanes can be changed. Used by the static segment validator,
 * the lane analysis (terrain rows) and the populator.
 */
export interface LayoutRules {
  readonly groundHeight: number;
  readonly playerWidth: number;
  /** Footprint depth along the track – the runner stands on ground anywhere under it. */
  readonly playerDepth: number;
  readonly standingHeight: number;
  readonly slidingHeight: number;
  readonly maxStepHeight: number;
  readonly jumpHeight: number;
  readonly doubleJumpHeight: number;
  /** Longest pit a single jump clears at start speed, safety margin included (meters). */
  readonly maxJumpGap: number;
  /**
   * Longest gap between two raised surfaces of the same height that a runner on top crosses
   * without a jump at start speed – it drops less than a step on the way (meters).
   */
  readonly maxRunAcrossGap: number;
  /** Derived jump physics (gravity, launch velocities) – reachability of double-jump steps. */
  readonly physics: JumpPhysics;
  /** Fixed step the PlayerController runs at (s) – its integration loses g·dt/2 of every launch speed (JumpReach). */
  readonly simulationStep: number;
  /** Fairness of steps that need a double jump (LevelConfig.terrain.doubleJumpStep). */
  readonly doubleJumpStep: {
    readonly minTriggerWindow: number;
    readonly minTakeOffWindow: number;
  };
  readonly maxRampSlope: number;
  readonly connectorClearance: number;
  readonly spawnClearance: number;
  readonly sampleStep: number;
  readonly rowMergeDistance: number;
  /** For checking hand-designed layouts with the SequenceValidator at both ends of the speed ramp. */
  readonly sequence: {
    readonly laneChangeDuration: number;
    readonly reactionTime: number;
    readonly maxAirTime: number;
    readonly minSpeed: number;
    readonly maxSpeed: number;
  };
}

export function layoutRulesFrom(world: WorldConfig, player: PlayerConfig, difficulty: DifficultyConfig, level: LevelConfig, simulationStep = 0): LayoutRules {
  const physics = deriveJumpPhysics(player);
  const jumpAirTime = (2 * physics.jumpVelocity) / physics.gravity;
  // Walking off a ledge, the runner falls one step in this time – its front must reach the far side by then.
  const stepDropTime = Math.sqrt((2 * player.maxStepHeight) / physics.gravity);
  return {
    groundHeight: world.groundHeight,
    playerWidth: player.collider.width,
    playerDepth: player.collider.depth,
    standingHeight: player.collider.standingHeight,
    slidingHeight: player.collider.slidingHeight,
    maxStepHeight: player.maxStepHeight,
    jumpHeight: player.jumpHeight,
    doubleJumpHeight: player.doubleJumpHeight,
    maxJumpGap: difficulty.speed.start * jumpAirTime - level.terrain.jumpGapSafety,
    maxRunAcrossGap: player.collider.depth + difficulty.speed.start * stepDropTime - level.terrain.runAcrossSafety,
    physics,
    simulationStep,
    doubleJumpStep: { ...level.terrain.doubleJumpStep },
    maxRampSlope: level.terrain.maxRampSlope,
    connectorClearance: level.terrain.connectorClearance,
    spawnClearance: level.terrain.spawnClearance,
    sampleStep: level.terrain.sampleStep,
    rowMergeDistance: level.terrain.rowMergeDistance,
    sequence: {
      laneChangeDuration: player.laneChangeDuration,
      reactionTime: difficulty.reactionTime,
      maxAirTime: physics.maxAirTime,
      minSpeed: difficulty.speed.start,
      maxSpeed: difficulty.speed.max,
    },
  };
}

/** Rules for the shipped configuration. */
export const DEFAULT_LAYOUT_RULES: LayoutRules = layoutRulesFrom(WORLD_CONFIG, PLAYER_CONFIG, DIFFICULTY_CONFIG, LEVEL_CONFIG, RENDERING_CONFIG.fixedTimeStep);
